/**
 * Sign-in page.
 *
 * The login name is the account's email address. A successful sign-in stores
 * the API token the rest of the app sends on every request; everything behind
 * ProtectedRoutes depends on it being there.
 */
import React from "react";
import axios from "axios";
import { useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  TextField,
  Stack,
  Button,
  Snackbar,
  Alert,
  Box,
  Paper,
  Typography,
  FormControl,
  IconButton,
  OutlinedInput,
  InputLabel,
  InputAdornment,
  Link,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { flattenDjangoRestError, copy as userCopy } from "../utils/userFacingMessages";
import { takeSessionEndedMessage } from "../helper/sessionExpiry";

function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // Why the previous session ended (sign-out after inactivity), shown once.
  const [sessionEnded] = useState(takeSessionEndedMessage);

  const showToast = useCallback((message) => {
    setToastMessage(message);
    setToastOpen(true);
  }, []);

  const handleCloseToast = (_, reason) => {
    if (reason === "clickaway") return;
    setToastOpen(false);
  };

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const normalizedIdentifier = (email || "").trim().toLowerCase();
    const data = {
      username: normalizedIdentifier,
      password: password,
    };
    try {
      const res = await axios.post("api/login", data);
      if (res.status === 200) {
        localStorage.setItem("username", res.data.user_info.username);
        localStorage.setItem("token", res.data.key);
        if (res.data.user_info?.id != null) {
          localStorage.setItem("userId", String(res.data.user_info.id));
        }
        axios.defaults.headers.common["Authorization"] = "Token " + res.data.key;
        navigate("/");
        window.location.reload();
      }
    } catch (err) {
      console.error("Login failed", err);
      const raw = err?.response?.data;
      let msg = flattenDjangoRestError(raw);
      if (!msg && !err.response) {
        msg = userCopy.profileSaveNetwork;
      }
      if (!msg) {
        msg = userCopy.loginFailed;
      }
      showToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={4}
      sx={{ mt: "2%", mb: 4 }}
    >
      <Box sx={{ width: "100%", maxWidth: "1150px", px: 2, boxSizing: "border-box" }}>
        <Stack
          sx={{
            width: "100%",
            py: 2,
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f6f8 0%, #ecedf1 100%)",
            border: "1px solid #dde1e7",
            boxSizing: "border-box",
          }}
        >
          <Box sx={{ px: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Enter your email and password to access your account.
            </Typography>
          </Box>
        </Stack>

        {sessionEnded ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
            {sessionEnded}
          </Alert>
        ) : null}

        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            width: "100%",
            mt: 4,
            mb: 2,
            borderRadius: 3,
            border: "1px solid #e7ebf3",
            backgroundColor: "#ffffff",
            p: { xs: 2, md: 3 },
            boxSizing: "border-box",
          }}
        >
          <Stack spacing={2.5} sx={{ maxWidth: "540px" }}>
            <TextField
              required
              id="email"
              label="Email"
              placeholder="you@example.com"
              onChange={handleEmailChange}
              value={email}
              type="email"
              autoComplete="email"
              fullWidth
              variant="outlined"
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel htmlFor="login-password">Password</InputLabel>
              <OutlinedInput
                id="login-password"
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                autoComplete="current-password"
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -0.5 }}>
              <Link
                component={RouterLink}
                to="/passwordforgotten"
                underline="hover"
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Forgot password?
              </Link>
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              endIcon={<LoginIcon />}
              sx={{
                height: "52px",
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                alignSelf: { xs: "stretch", sm: "flex-start" },
                px: 3,
                mt: 0.5,
              }}
            >
              {submitting ? "Signing in…" : "Log in"}
            </Button>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={8000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseToast}
          severity="error"
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default Login;
